import { Group, TextInput } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { Search } from "lucide-react";
import { useState, type ReactNode } from "react";

import classes from "./SearchToolbar.module.css";

export interface ToolbarSearch {
  /** The search's label, read by a screen reader, and its placeholder. */
  label: string;
  /** The search as the URL holds it. */
  value: string;
  /** Called `delay` ms after the typing stops. */
  onChange: (value: string) => void;
  /** 500 by default, for a search the server runs; 0 to filter a list already read per key. */
  delay?: number;
}

export interface SearchToolbarProps {
  /** Rendered only where the list route takes a query (design-system.md 3.5). */
  search?: ToolbarSearch;
  /** Beside the search: filters. */
  left?: ReactNode;
  /** At the right: the page's main action, such as "Add Brand". */
  right?: ReactNode;
}

/** A list page's toolbar: a 350px search, filters beside it and actions at the right. */
export function SearchToolbar({ search, left, right }: SearchToolbarProps) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm" className={classes.toolbar}>
      <Group gap="sm" wrap="nowrap">
        {search === undefined ? null : <SearchInput search={search} />}
        {left}
      </Group>
      {right}
    </Group>
  );
}

function SearchInput({ search }: { search: ToolbarSearch }) {
  const [text, setText] = useState(search.value);
  const [focused, setFocused] = useState(false);
  // The box follows the URL when something else changes it (a reset, going back), but not while
  // the user types: then the URL trails the box by the debounce.
  const [seen, setSeen] = useState(search.value);
  if (search.value !== seen) {
    setSeen(search.value);
    if (!focused) setText(search.value);
  }
  const delay = search.delay ?? 500;
  const settle = useDebouncedCallback((value: string) => search.onChange(value.trim()), delay);
  return (
    <TextInput
      aria-label={search.label}
      placeholder={search.label}
      leftSection={<Search size={16} />}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(event) => {
        const value = event.currentTarget.value;
        setText(value);
        if (delay === 0) search.onChange(value.trim());
        else settle(value);
      }}
      className={classes.search}
    />
  );
}
